import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  List,
  ListItem,
  ListIcon,
  useToast,
} from "@chakra-ui/react";
import { FaCircle } from "react-icons/fa";
import CampaignPreviewModal from "./CampaignPreviewModal";
import { cqAPI } from "../../lib/Auth";

function CampaignSummary({ campaigns, onCampaignUpdate, onSendTest, onBack, onScheduleSuccess }) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const toast = useToast();

  const totalPatients = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return 0;
    const patientIdSets = campaigns.map(
      (c) => new Set(c.settings?.filter?.patient_ids?.list?.map((p) => p.value) || [])
    );
    const allPatients = new Set();
    patientIdSets.forEach((set) => set.forEach((id) => allPatients.add(id)));
    return allPatients.size;
  }, [campaigns]);

  const campaignsByType = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return {};
    const grouped = {};
    campaigns.forEach((c) => {
      const type = c.type || "unknown";
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(c);
    });
    return grouped;
  }, [campaigns]);

  const types = useMemo(() => Object.keys(campaignsByType), [campaignsByType]);

  const handleEditClick = () => {
    setIsPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setIsPreviewOpen(false);
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    }
  };

  const handleScheduleClick = async () => {
    if (!campaigns || campaigns.length === 0) return;

    setIsScheduling(true);
    try {
      const campaignIds = campaigns.map((c) => c._id);
      const response = await cqAPI.post("/campaigns/activate", { campaignIds });

      if (response.type === "ok") {
        const { modifiedCount, matchedCount, failedCount } = response.data;

        if (modifiedCount > 0) {
          toast({
            title: "Campaigns Scheduled",
            description: `${modifiedCount} campaign(s) have been activated successfully. You can review and edit these Patient Outreach under Patient Outreach -> Manual, ensure to review the date for this campaign`,
            status: "success",
            duration: 9000,
            isClosable: true,
          });
        } else if (matchedCount > 0 && modifiedCount === 0) {
          toast({
            title: "Already Scheduled",
            description: "These campaigns are already active and scheduled.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "No Campaigns Found",
            description: "No matching campaigns were found to schedule.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }

        if (failedCount > 0) {
          toast({
            title: "Some Campaigns Could Not Be Activated",
            description: `We couldn't activate ${failedCount} of the campaigns. Please navigate to Patient Outreach and activate your campaign in the Manual Campaigns section.`,
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
        }

        if (onScheduleSuccess && modifiedCount > 0) {
          onScheduleSuccess();
        }
      } else {
        throw new Error(response.msg || "Failed to activate campaigns");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to schedule campaigns",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsScheduling(false);
    }
  };

  if (!campaigns || campaigns.length === 0) {
    return null;
  }

  return (
    <Box
      p={4}
      bg="gray.50"
      borderRadius="md"
      border="1px"
      borderColor="gray.200"
      mt={4}
    >
      <VStack align="start" spacing={4}>
        <HStack justify="space-between" w="100%">
          <Text fontWeight="bold" fontSize="lg">
            AI Patient outreach settings summary
          </Text>
        </HStack>

        <Text>
          AI will reach out to your <strong>{totalPatients}</strong> patients
          through:
        </Text>

        <VStack align="start" spacing={3} w="100%">
          {types.map((type) => (
            <Box key={type}>
              <Text fontWeight="semibold" textTransform="capitalize" mb={1}>
                {type}
              </Text>
              <List spacing={1} pl={2}>
                {campaignsByType[type].map((campaign) => (
                  <ListItem key={campaign._id} display="flex" alignItems="center">
                    <ListIcon as={FaCircle} color="teal.500" boxSize={2} />
                    <Text>{campaign.name || "Untitled Campaign"}</Text>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </VStack>

        <Flex gap={3} mt={2}>
          <Button
            colorScheme="teal"
            variant="outline"
            size="sm"
            onClick={handleEditClick}
          >
            Preview
          </Button>
        </Flex>

        <Flex gap={3} mt={2}>
          <Button
            colorScheme="teal"
            size="sm"
            onClick={handleScheduleClick}
            isLoading={isScheduling}
          >
            Schedule
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackClick}
            isDisabled={isScheduling}
          >
            Back
          </Button>
        </Flex>
      </VStack>

      <CampaignPreviewModal
        isOpen={isPreviewOpen}
        onClose={handlePreviewClose}
        campaigns={campaigns}
        onCampaignUpdate={onCampaignUpdate}
        onSendTest={onSendTest}
      />
    </Box>
  );
}

export default CampaignSummary;